import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getNotifications, readAllNotifications } from '../../redux/actions/notificationActions';
import { useTheme } from '../../hooks/useTheme'; // Importation du hook useTheme


const Navbar = ({ onMenuClick, user, onLogout, adminMode = false, title = "Alphafusion File Manager" }) => {
  const dispatch = useDispatch();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  useTheme();
  
  // Récupérer les notifications depuis le state Redux
  const { notifications = [] } = useSelector(state => state.notifications);
  
  // Charger les notifications au montage du composant
  useEffect(() => {
    dispatch(getNotifications());
  }, [dispatch]);
  
  const unreadCount = notifications.filter(n => !n.is_read).length;

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
    if (notificationsOpen) setNotificationsOpen(false);
  };

  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
    if (userMenuOpen) setUserMenuOpen(false);
  };
  
  const handleMarkAllAsRead = () => {
    dispatch(readAllNotifications());
  };

  const getNotifIcon = (type) => {
    switch (type) {
      case 'submission_status': return 'description';
      case 'order_status': return 'shopping_cart';
      case 'new_submission': return 'upload_file';
      case 'new_order': return 'add_shopping_cart';
      default: return 'notifications';
    }
  };

  const getNotifIconClass = (type) => {
    switch (type) {
      case 'submission_status': return 'notif-icon--submission';
      case 'order_status': return 'notif-icon--order';
      case 'new_submission': return 'notif-icon--new-submission';
      case 'new_order': return 'notif-icon--new-order';
      default: return 'notif-icon--default';
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Date inconnue";
      }
      
      // Format date according to locale preferences
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date inconnue";
    }
  };

  // Fermer les menus lorsque l'utilisateur clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && !event.target.closest('.user-profile')) {
        setUserMenuOpen(false);
      }
      if (notificationsOpen && !event.target.closest('.notification-container')) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen, notificationsOpen]);

  return (
    <nav className={`navbar ${adminMode ? 'navbar-admin' : ''}`}>
      <div className="navbar-left">
        <button className="menu-button" onClick={onMenuClick} aria-label="Menu principal">
          <i className="material-icons">menu</i>
        </button>
        <h1 className="navbar-title">{title}</h1>
      </div>

      <div className="navbar-right">
        
        <div className="notification-container">
          <button 
            className="notification-button" 
            onClick={toggleNotifications}
            aria-label={`Notifications (${unreadCount} non lues)`}
          >
            <i className="material-icons">notifications</i>
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </button>
          {notificationsOpen && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <h3>Notifications</h3>
                {unreadCount > 0 && (
                  <button 
                    className="mark-all-read" 
                    onClick={handleMarkAllAsRead}
                    aria-label="Marquer toutes les notifications comme lues"
                  >
                    Tout marquer comme lu
                  </button>
                )}
              </div>
              <div className="notification-list">
                {notifications.length > 0 ? (                  
                  notifications.slice(0, 5).map(notification => (
                    <div key={notification.id} className={`notification-item ${!notification.is_read ? 'unread' : ''}`}>
                      <i className={`material-icons notification-icon ${getNotifIconClass(notification.type)}`}>
                        {getNotifIcon(notification.type)}
                      </i>
                      <div className="notification-content">
                        <p>{notification.message}</p>
                        <span className="notification-time">
                          {formatDate(notification.created_at)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="notification-empty">
                    <p>Aucune notification</p>
                  </div>
                )}
              </div>
              <div className="notification-footer">
                <Link 
                  to={adminMode ? "/admin/notifications" : "/notifications"} 
                  onClick={() => setNotificationsOpen(false)}
                  className="view-all-link"
                >
                  Voir toutes les notifications
                </Link>
              </div>
            </div>
          )}
        </div>

        
        <div className="user-profile">
          <button 
            className="user-button" 
            onClick={toggleUserMenu}
            aria-label="Menu utilisateur"
          >
            <div className="user-avatar">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <span className="user-name">
              {user?.firstName} {user?.lastName}
            </span>
            <i className="material-icons">arrow_drop_down</i>
          </button>
          
          {userMenuOpen && (
            <div className="user-dropdown">
              <div className="user-dropdown-header">
                <div className="user-dropdown-avatar">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
                <div className="user-dropdown-info">
                  <h4>{user?.firstName} {user?.lastName}</h4>
                  <p>{user?.email}</p>
                  <span className="user-role">{user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}</span>
                </div>
              </div>
              <div className="user-dropdown-menu">
                <Link to="/profile" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                  <i className="material-icons">person</i>
                  Mon profil
                </Link>
                {user?.role === 'admin' && (
                  <Link to="/admin" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                    <i className="material-icons">admin_panel_settings</i>
                    Administration
                  </Link>
                )}
                <Link to="/orders" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                  <i className="material-icons">shopping_cart</i>
                  Mes commandes
                </Link>
                <button className="dropdown-item" onClick={onLogout}>
                  <i className="material-icons">logout</i>
                  Déconnexion
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;